import Text from "@/components/Text";
import IconBackground from "@/components/IconBackground";
import classes from "./index.module.css";
import TooltipIcon from "@/components/TooltipIcon";
import { useTranslation } from "@/i18n/client";
import CommodityIcon from "@/components/CommodityIcon";
import { Process as ProcessContract, ProcessSpecification, CommodityEnum } from "@/types/contract";
import { Process } from "@/types/enumerations";
import { Product } from "@/types/enumerations";
import { CarElectric, Contract } from "@/components/Icons";
import type { ProcessReasonType } from "@/types/processReason";
import { RecontractingVariant } from "@/types/recontractingRequest";
import { isS3PreviousCustomer } from "@/screens/Welcome/helpers";
import { useIsContractRequest } from "@/hooks/useIsContractRequest";

interface TitleProps {
  commodity: CommodityEnum;
  process: Process;
  product?: Product | null;
  processSpecification?: ProcessSpecification;
  selfService: boolean;
  processReason?: ProcessReasonType;
  recontractingVariant?: RecontractingVariant;
  phase?: string | null;
  contractStatus?: string | null;
}

const Title = ({
  commodity,
  process,
  product,
  processSpecification,
  selfService,
  processReason,
  recontractingVariant,
  phase,
  contractStatus, // eslint-disable-next-line sonarjs/cognitive-complexity
}: TitleProps) => {
  const { t } = useTranslation(["hints", "welcome"]);
  const isVRTProcess = process.value === ProcessContract.VRB && product?.value.forbiddenOverflow;

  const isVRBProductChange = process.value === ProcessContract.VRB && processSpecification === ProcessSpecification.ZSP;
  const isS1Process = process.value === ProcessContract.SUPPLIER_CHANGE;
  const isTCRProcess = process.value === ProcessContract.TCR;

  const isR1ProcessSelfService = process.value === ProcessContract.NEW_POINT && selfService === true;
  const isZSPProcess = process.value === ProcessContract.RECONTRACTING;
  const isMSProcess = process.value === ProcessContract.MOBILITY_SERVICES;
  const isS2Process = process.value === ProcessContract.DPI;
  const isZDPProcess = process.value === ProcessContract.ZDP;
  const isPSDraft = process.value === ProcessContract.PS && phase === "draft";
  const isS3NewCustomerProcess = isS3PreviousCustomer(process.value, contractStatus, phase);
  const isPSProcess = process.value === ProcessContract.PS;
  const isContractRequest = useIsContractRequest();
  const isPSRequest = process.value === ProcessContract.PS && isContractRequest;

  const showTitleWithHint =
    !isS1Process &&
    !isR1ProcessSelfService &&
    !isTCRProcess &&
    !isZSPProcess &&
    !isMSProcess &&
    !isZDPProcess &&
    !isS2Process &&
    !isPSProcess;

  const getTitle = (process: Process, processReason?: ProcessReasonType) => {
    if (isS3NewCustomerProcess) {
      return t("welcome:titleS3NewCustomer");
    }
    if (isPSDraft) {
      return t("welcome:titlePSDraft");
    }
    if (isVRBProductChange) {
      return t("welcome:titleVRBProductChange");
    }
    if (isVRTProcess) {
      return t("welcome:titleVRT");
    }
    if (recontractingVariant === RecontractingVariant.ACCORD) {
      return t("welcome:titleAccord");
    }
    if (isTCRProcess && processReason === "demontage_01") {
      return t("welcome:titleTCRDismantling");
    }
    if (isTCRProcess && processReason === "demontage_02") {
      return t("welcome:titleTCRDeath");
    } else {
      return process.nameForCustomers ?? process.name;
    }
  };

  const hint = isS3NewCustomerProcess
    ? t("welcome.process.S3NewCustomer")
    : isVRBProductChange
    ? t("welcome.process.VRBProductChange")
    : isVRTProcess
    ? t("welcome.process.VRT")
    : t(`welcome.process.${process.value}`);

  return (
    <header className={classes.header}>
      <IconBackground withBorder width="var(--icon-bg-size)" height="var(--icon-bg-size)">
        {isTCRProcess || isPSRequest ? (
          <Contract width="var(--icon-size)" height="var(--icon-size)" />
        ) : isMSProcess ? (
          <CarElectric width="var(--icon-size)" height="var(--icon-size)" />
        ) : (
          <CommodityIcon commodity={commodity} width="var(--icon-size)" height="var(--icon-size)" />
        )}
      </IconBackground>
      <span className={showTitleWithHint ? classes.rightWithHint : classes.right}>
        <Text variant="h3">{getTitle(process, processReason)}</Text>
        {isS1Process && <Text variant="subheadline_bold">{t("welcome:subtitleS1")}</Text>}
        {isMSProcess && <Text variant="subheadline_bold">{t("welcome:subtitleMS")}</Text>}
        {isS2Process && <Text variant="subheadline_bold">{t("welcome:subtitleS2")}</Text>}
        {showTitleWithHint && <TooltipIcon size={32} label={hint} iconSize={24} />}
      </span>
    </header>
  );
};

export default Title;
